import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchMessageThreads,
  fetchSharedReviews,
  fetchTasks,
  fetchVeteransAffairs,
} from "../service/home";

/**
 * The matter home page's activity section.
 *
 * Deliberately separate from MatterDetailsSlice: the matter tree and this
 * section refresh on completely different triggers, and sharing a slice
 * would mean every folder expansion invalidated the activity lists (and
 * vice versa).
 *
 * The four lists read the same endpoints Home reads, with a `matter_id`
 * that swaps the question from "what is mine" to "what is on this case",
 * so there is one fetcher and one row shape per domain rather than a
 * parallel set for the matter page.
 *
 * All four load together and are shown at once (Matthew, 2026-07-30).
 * There is no active-section concept: each list is its own component with
 * its own filters, paging and sort, and none of them can hide another.
 * Their counts therefore come straight off each response, which is why
 * there is no separate summary request -- a count read from the list it
 * labels cannot disagree with it.
 */

export const MATTER_ACTIVITY_SECTIONS = ["va", "tasks", "reviews", "messages"];

const FETCHERS = {
  va: fetchVeteransAffairs,
  tasks: fetchTasks,
  reviews: fetchSharedReviews,
  messages: fetchMessageThreads,
};

// Mirrors the Home defaults, with three differences. `role` is absent
// because the matter is the scope; the page size is smaller because these
// are cards on a dashboard rather than full-page lists; and work opens on
// ALL rather than ACTIVE, because a case view is a record of what has
// happened and a finished assignment is part of that (Matthew,
// 2026-07-30). Home stays on ACTIVE, where the question is what is left.
const DEFAULT_FILTERS = {
  va: {
    sort_by: "sent_on", sort_direction: "desc", page: 1, page_size: 10,
    status: "", due_on: "", search: "", chip_status: "ALL",
  },
  tasks: { page: 1, page_size: 10, search: "", chip_status: "ALL" },
  reviews: {
    sort_by: "file_name", sort_direction: "asc", page: 1, page_size: 10,
    status: "", due_on: "", search: "", chip_status: "ALL",
  },
  messages: { page: 1, page_size: 10, search: "", chip_status: "all" },
};

const EMPTY_LIST = { results: [], count: 0, chip_counts: {}, filter_chips: {} };

const bySection = (value) => ({
  va: value, tasks: value, reviews: value, messages: value,
});

const clone = (source) => JSON.parse(JSON.stringify(source));

/**
 * One thunk for all four lists. They differ in their row shape, not in
 * how they are loaded, so a `section` argument beats four copies of this.
 */
export const fetchMatterActivityList = createAsyncThunk(
  "matterActivity/list",
  async ({ matterId, section }, { getState, rejectWithValue }) => {
    try {
      const filters = getState().matterActivity.filters[section];
      return await FETCHERS[section]({ ...filters, matter_id: matterId });
    } catch (error) {
      return rejectWithValue(error?.message || "Unable to load activity");
    }
  }
);

// A response that lands after the user has moved to another matter must be
// dropped, not rendered: it would put another case's rows under this
// case's heading.
const isStale = (state, action) => state.matterId !== action.meta.arg.matterId;

const initialState = {
  matterId: null,
  lists: bySection(EMPTY_LIST),
  status: bySection("idle"),
  errors: bySection(null),
  filters: clone(DEFAULT_FILTERS),
};

const MatterActivitySlice = createSlice({
  name: "matterActivity",
  initialState,
  reducers: {
    setMatterActivityMatter: (state, action) => {
      if (state.matterId === action.payload) {
        return;
      }
      // Full reset, including filters: a chip or search term carried over
      // from the previous case would silently hide rows on this one.
      state.matterId = action.payload;
      state.lists = bySection(EMPTY_LIST);
      state.status = bySection("idle");
      state.errors = bySection(null);
      state.filters = clone(DEFAULT_FILTERS);
    },
    setMatterActivityFilters: (state, action) => {
      const { section, filters } = action.payload;
      state.filters[section] = { ...state.filters[section], ...filters };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMatterActivityList.pending, (state, action) => {
        state.status[action.meta.arg.section] = "loading";
      })
      .addCase(fetchMatterActivityList.fulfilled, (state, action) => {
        if (isStale(state, action)) {
          return;
        }
        const { section } = action.meta.arg;
        state.status[section] = "succeeded";
        state.errors[section] = null;
        state.lists[section] = action.payload.data;
      })
      .addCase(fetchMatterActivityList.rejected, (state, action) => {
        if (isStale(state, action)) {
          return;
        }
        const { section } = action.meta.arg;
        state.status[section] = "failed";
        state.errors[section] = action.payload;
        // Empty rather than the last good page: stale rows after a failed
        // refresh read as current, and the user cannot tell. Only this
        // list empties -- the other three are unaffected, which is the
        // point of loading them independently.
        state.lists[section] = EMPTY_LIST;
      });
  },
});

export const {
  setMatterActivityMatter,
  setMatterActivityFilters,
} = MatterActivitySlice.actions;

export default MatterActivitySlice.reducer;
